import { useState, useEffect, useRef } from "react"
import { FaPlus, FaChevronDown, FaChevronRight, FaEllipsisV, FaEdit, FaTrash } from "react-icons/fa"
import DeleteConfirmationDialog from "./DeleteConfirmationDialog"
import "./SubTypeComponent.css";

const SubTypeComponent = ({ types = [], depth = 1, onSave, onEdit, onDelete }) => {

    const [openTypes, setOpenTypes] = useState(new Set())
    const [editingType, setEditingType] = useState(null)
    const [currentTitle, setCurrentTitle] = useState("")
    const [isOptionsOpen, setIsOptionsOpen] = useState(null)
    const [mode, setMode] = useState("create")
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [deleteTypeId, setDeleteTypeId] = useState(null)
    const optionsRef = useRef(null)

    const handleToggleSubtype = (typeId) => {
        setOpenTypes((prevOpenTypes) => {
            const updatedOpenTypes = new Set(prevOpenTypes)
            updatedOpenTypes.has(typeId)
                ? updatedOpenTypes.delete(typeId)
                : updatedOpenTypes.add(typeId)
            return updatedOpenTypes
        })
    }

    const handleEdit = (typeId) => {
        const type = types.find((type) => type.id === typeId)
        setEditingType(typeId)
        setCurrentTitle(type?.title || "")
        setIsOptionsOpen(null)
        setMode("update")
    }

    const handleCreateNew = (typeId) => {
        setEditingType(typeId)
        setCurrentTitle("")
        setIsOptionsOpen(null)
        setMode("create")
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSave(e, editingType)
        }
    }

    const handleSave = (e, parentId) => {
        e.preventDefault()
        if (currentTitle.trim()) {
            onSave(e, currentTitle, parentId)
            resetEditing()
        }
    }

    const handleUpdate = (e, typeId) => {
        e.preventDefault()
        if (currentTitle.trim()) {
            onEdit(e, typeId, currentTitle)
            resetEditing()
        }
    }

    const handleCancel = (e) => {
        e.preventDefault()
        resetEditing()
    }

    const resetEditing = () => {
        setCurrentTitle("")
        setEditingType(null)
        setMode("create")
    }

    const handleOptionsToggle = (typeId) => {
        setIsOptionsOpen((prev) => (prev === typeId ? null : typeId))
    }

    const handleClickOutside = (e) => {
        if (optionsRef.current && !optionsRef.current.contains(e.target)) {
            setIsOptionsOpen(null)
        }
    }

    useEffect(() => {
        if (isOptionsOpen !== null) {
            document.addEventListener("mousedown", handleClickOutside)
        } else {
            document.removeEventListener("mousedown", handleClickOutside)
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [isOptionsOpen])

    const isLeafNode = (type) => !type.children || type.children.length === 0

    const renderSubTypes = (type, index, currentDepth) => {
        const currentCounter = `${currentDepth}.${index + 1}`
        const indentStyle = { marginLeft: `${(currentDepth - 1) * 5}px` }

        return (
            <div key={type.id} className="SubTypeComponentcontainer" style={indentStyle}>
                <div className="flex-row">
                    <div className="flex-center">
                        <span className="font-semibold text-lg text-gray-700">
                            {currentCounter}
                        </span>
                        <span className="ml-4 text-gray-900">
                            {(type.title)}
                        </span>
                    </div>
                    <div className="relative flex items-center space-x-3">
                        {type.children && (
                            <div
                                onClick={() => handleToggleSubtype(type.id)}
                                className="cursor-pointer" style={{color: '#11a7cc'}} 
                                title={openTypes.has(type.id) ? "Collapse" : "Expand"}
                            >
                                {openTypes.has(type.id) ? <FaChevronDown /> : <FaChevronRight />}
                            </div>
                        )}
                        <div className="cursor-pointer text-gray-400">
                            <FaEllipsisV onClick={() => handleOptionsToggle(type.id)} />
                        </div>
                        {isOptionsOpen === type.id && (
                            <div
                                ref={optionsRef}
                                className="absolute top-0 right-0 mt-2 bg-white border rounded-md shadow-lg z-20">
                                <button
                                style={{justifyContent: 'center', alignItems: 'center'}}
                                    onClick={() => {
                                        setIsOptionsOpen(null)
                                        handleCreateNew(type.id)
                                    }}
                                    className="flex items-center px-4 py-2 text-blue-500 editbuttons-full text-left"
                                >
                                    <FaPlus className="mr-2" />
                                    Add
                                </button>
                                <button
                                style={{justifyContent: 'center', alignItems: 'center'}}
                                    onClick={() => {
                                        setIsOptionsOpen(null)
                                        handleEdit(type.id)
                                    }}
                                    className="flex items-center px-4 py-2 text-green-500 editbuttons-full text-left"
                                >
                                    <FaEdit className="mr-2" />
                                    Edit
                                </button>
                                <button
                                style={{justifyContent: 'center', alignItems: 'center'}}
                                    onClick={() => {
                                        setIsOptionsOpen(null)
                                        setDeleteTypeId(type.id)
                                        setIsDeleteDialogOpen(true)
                                    }}
                                    className="flex items-center px-4 py-2 text-red-500 editbuttons-full text-left"
                                >
                                    <FaTrash className="mr-2" />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {editingType === type.id && (
                    <div className="mt-4 p-4 border rounded-md bg-gray-50">
                        <div
                        style={{marginBottom: '2vh'}}
                            className={`flex flex-col ${
                                mode === "update" && isLeafNode(type)
                                    ? "sm:flex-row sm:items-center gap-4 mb-2"
                                    : "mb-2"
                            }`}
                        >
                            <div
                            style={{display: 'flex', justifyContent: 'flex-start', flexDirection: 'column', gap: '2vh'}}
                                className={`${
                                    mode === "update" && isLeafNode(type)
                                        ? "w-full sm:w-1/2"
                                        : "w-full"
                                }`}
                            >
                                <label
                                    htmlFor="title"
                                    className="block text-sm font-medium text-gray-700"
                                >
                                    Title
                                </label>
                                <input
                                    id="title"
                                    type="text"
                                    value={currentTitle}
                                    onChange={(e) => setCurrentTitle(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter new title"
                                    className="border p-2 rounded w-full focus:outline-none focus:border-blue-400"
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleCancel}
                                className="button button-cancel">
                                Cancel
                            </button>
                            <button
                                onClick={(e) =>
                                    mode === "create"
                                        ? handleSave(e, type.id)
                                        : handleUpdate(e, type.id)
                                }
                                 className="button button-save"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                )}
                {openTypes.has(type.id) && type.children && (
                    <div className="ml-1">
                        <SubTypeComponent
                            types={type.children}
                            depth={currentDepth + 1}
                            onSave={onSave}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    </div>
                )}
            </div>
        )
    }

    const handleDeleteDialogConfirm = () => {
        if (deleteTypeId) {
            onDelete(deleteTypeId)
            setDeleteTypeId(null)
        }
        setIsDeleteDialogOpen(false)
    }

    const handleDeleteDialogClose = () => {
        setDeleteTypeId(null)
        setIsDeleteDialogOpen(false)
    }

    return (
        <div>
            <DeleteConfirmationDialog
                isOpen={isDeleteDialogOpen}
                onClose={handleDeleteDialogClose}
                onConfirm={handleDeleteDialogConfirm}
            />

            {types?.map((type, index) => renderSubTypes(type, index, depth))}
        </div>
    )
}

export default SubTypeComponent;
